import {
  Component,
  OnInit,
  Output,
  EventEmitter,
  ViewChild,
  AfterViewInit,
  input,
  effect,
} from "@angular/core";
import { MatCheckboxChange } from "@angular/material/checkbox";
import { MatSort, Sort, SortDirection } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";

/** Column type hints for sorting */
export type ColumnSortType = "string" | "number" | "date";

/** Column sorting configuration */
export interface ColumnSortConfig {
  [columnKey: string]: ColumnSortType;
}

export interface CustomStyleColumnConfig {
  [columnKey: string]: string;
}

@Component({
  selector: "app-table-grid",
  templateUrl: "./table-grid.component.html",
  styleUrls: ["./table-grid.component.scss"],
  standalone: false,
})
export class TableGridComponent implements OnInit, AfterViewInit {
  data = input<any[]>([]);
  headers = input<any[]>([]);
  footer = input<string>("");
  buttonViewText = input<string>("");
  customStyleColumn = input<string>("");
  customStylepProp = input<string>("");
  customStyleColumns = input<CustomStyleColumnConfig>({});
  customBoolColumn = input<string>("");
  customBoolTrueColor = input<string>("");
  customBoolShowFalseIcon = input<boolean>(false);
  customBoolFalseIcon = input<string>("error");
  customBoolFalseIconColor = input<string>("warn");
  /** Column that always renders check_small when true and error (warn) when false */
  customBoolWithFalseColumn = input<string>("");
  customStyle = input<string>("");
  displayedColumns = input<any[]>([]);
  columnsLink = input<string[]>([]);
  columnWidths = input<string[]>([]);
  isLoading = input<boolean>(true);
  usingAutoLoad = input<boolean>(false);
  skeletonRowsCount = input<number>(5);
  selectColumn = input<string>("");
  selectEnabledColumn = input<string>("");

  /** Enable sorting functionality - default false to not affect existing tables */
  enableSorting = input<boolean>(false);
  /** Server-side sorting mode: emit sort events only, do not sort current page in-memory */
  serverSideSorting = input<boolean>(false);
  /** Column type configuration for type-aware sorting */
  columnSortTypes = input<ColumnSortConfig>({});
  /** Default sort column */
  defaultSortColumn = input<string>("");
  /** Default sort direction */
  defaultSortDirection = input<SortDirection>("desc");

  /* output */
  @Output() buttonClicked: EventEmitter<any> = new EventEmitter<any>();
  @Output() rowClicked: EventEmitter<any> = new EventEmitter<any>();
  @Output() cellClicked: EventEmitter<any> = new EventEmitter<any>();
  @Output() sortChanged: EventEmitter<Sort> = new EventEmitter<Sort>();
  @Output() selectionChanged: EventEmitter<any[]> = new EventEmitter<any[]>();

  private sort?: MatSort;

  @ViewChild(MatSort)
  set matSort(sort: MatSort | undefined) {
    this.sort = sort;
    this.initializeSorting();
  }

  selectedRow: any;
  public selectedRows = new Set<any>();
  public columnsToDisplay: any[] = [];
  public skeletonRows: any[] = []; // Array to drive skeleton loop
  public dataSource = new MatTableDataSource<any>();

  constructor() {
    // React to displayedColumns signal changes
    effect(() => {
      this.columnsToDisplay = this.displayedColumns().slice();
    });

    // React to skeletonRowsCount signal changes
    effect(() => {
      this.skeletonRows = new Array(this.skeletonRowsCount()).fill(null);
    });

    // React to data signal changes - update dataSource
    effect(() => {
      const newData = this.data();
      if (this.enableSorting()) {
        this.dataSource.data = newData;
      }
      this.syncSelectionWithData();
    });
  }

  ngOnInit(): void {
    this.columnsToDisplay = this.displayedColumns().slice();
    this.skeletonRows = new Array(this.skeletonRowsCount()).fill(null);
    if (this.enableSorting()) {
      this.dataSource.data = this.data();
    }
  }

  ngAfterViewInit(): void {
    this.initializeSorting();
  }

  private initializeSorting(): void {
    if (!this.enableSorting() || this.serverSideSorting() || !this.sort) {
      return;
    }

    this.setupSortingDataAccessor();
    this.dataSource.sort = this.sort;
  }

  /** Configure type-aware sorting based on columnSortTypes */
  private setupSortingDataAccessor(): void {
    const sortTypes = this.columnSortTypes();

    this.dataSource.sortingDataAccessor = (
      item: any,
      property: string,
    ): string | number => {
      const columnType = sortTypes[property] || "string";
      const value = item[property];

      switch (columnType) {
        case "number":
          // Support display fields (e.g. durationText) that should sort by a raw numeric field.
          if (property.endsWith("Text")) {
            const rawNumberField = property.replace(/Text$/, "");
            const rawNumberValue = item[rawNumberField];
            if (typeof rawNumberValue === "number") {
              return rawNumberValue;
            }
          }
          return typeof value === "number" ? value : parseFloat(value) || 0;
        case "date":
          // Handle date fields - look for raw date field if display field is used
          const rawDateField = property.replace("_local", "");
          const dateValue = item[rawDateField] || value;
          return dateValue ? new Date(dateValue).getTime() : 0;
        case "string":
        default:
          // Support display fields (e.g. durationText) that should sort by a raw numeric field.
          if (property.endsWith("Text")) {
            const rawNumberField = property.replace(/Text$/, "");
            const rawNumberValue = item[rawNumberField];
            if (typeof rawNumberValue === "number") {
              return rawNumberValue;
            }
          }
          return (value || "").toString().toLowerCase();
      }
    };
  }

  /** Handle sort change events */
  onSortChange(sort: Sort): void {
    this.sortChanged.emit(sort);
  }

  buttonClick(element: any) {
    this.buttonClicked.emit(element);
  }

  rowClick(row: any) {
    this.selectedRow = row;
    this.rowClicked.emit(row);
  }

  cellClick(element: any, column: any) {
    this.cellClicked.emit({ element: element, column: column });
  }

  toggleRowSelection(event: Event, element: any) {
    event.stopPropagation();
    if (this.selectedRows.has(element)) {
      this.selectedRows.delete(element);
    } else {
      this.selectedRows.add(element);
    }
    this.selectionChanged.emit(Array.from(this.selectedRows));
  }

  toggleRowSelectionByChecked(checked: boolean, element: any) {
    if (!this.isRowSelectionEnabled(element)) {
      return;
    }

    if (checked) {
      this.selectedRows.add(element);
    } else {
      this.selectedRows.delete(element);
    }
    this.selectionChanged.emit(Array.from(this.selectedRows));
  }

  toggleAllSelection(event: MatCheckboxChange) {
    const rows = this.getSelectableRows(
      this.enableSorting() ? this.dataSource.data : this.data(),
    );

    if (event.checked) {
      rows.forEach((row) => this.selectedRows.add(row));
    } else {
      rows.forEach((row) => this.selectedRows.delete(row));
    }

    this.selectionChanged.emit(Array.from(this.selectedRows));
  }

  isAllRowsSelected(): boolean {
    const rows = this.getSelectableRows(
      this.enableSorting() ? this.dataSource.data : this.data(),
    );
    return rows.length > 0 && rows.every((row) => this.selectedRows.has(row));
  }

  isSomeRowsSelected(): boolean {
    const rows = this.getSelectableRows(
      this.enableSorting() ? this.dataSource.data : this.data(),
    );
    const selectedInView = rows.filter((row) => this.selectedRows.has(row)).length;
    return selectedInView > 0 && selectedInView < rows.length;
  }

  isRowSelectionEnabled(element: any): boolean {
    const selectionColumn = this.selectEnabledColumn();
    return !selectionColumn || !!element?.[selectionColumn];
  }

  hasSelectableRows(): boolean {
    const rows = this.enableSorting() ? this.dataSource.data : this.data();
    return this.getSelectableRows(rows).length > 0;
  }

  private getSelectableRows(rows: any[]): any[] {
    const selectionColumn = this.selectEnabledColumn();
    if (!selectionColumn) {
      return rows;
    }

    return rows.filter((row) => !!row?.[selectionColumn]);
  }

  private syncSelectionWithData() {
    const rows = this.enableSorting() ? this.dataSource.data : this.data();
    const currentRows = new Set(rows);
    let changed = false;

    Array.from(this.selectedRows).forEach((row) => {
      if (!currentRows.has(row) || !this.isRowSelectionEnabled(row)) {
        this.selectedRows.delete(row);
        changed = true;
      }
    });

    if (changed) {
      this.selectionChanged.emit(Array.from(this.selectedRows));
    }
  }

  isRowSelected(element: any): boolean {
    return this.selectedRows.has(element);
  }

  clearSelection() {
    this.selectedRows.clear();
    this.selectionChanged.emit([]);
  }

  getColumnMinWidth(index: number): string {
    const widths = this.columnWidths();
    if (widths && widths[index] && widths[index] !== "*") {
      return widths[index] + "px";
    }
    return "";
  }

  getCustomColumnClass(element: any, column: string): string {
    if (!element) {
      return "";
    }

    const customStyleProp = this.customStyleColumns()[column];
    if (customStyleProp && element[customStyleProp]) {
      return element[customStyleProp];
    }

    if (column === this.customStyleColumn() && element[this.customStylepProp()]) {
      return element[this.customStylepProp()];
    }

    return "";
  }
}
